/**
 * Which models this tool can reason with, and what each one can actually do.
 *
 * This file imports nothing at runtime, deliberately. `persist.ts` validates a
 * stored model id during the boot chain and `store.ts` reads this table at
 * module evaluation; a registry that imported anything could join the cycle
 * `session.ts`'s header describes, and one living in `engine/` would pull both
 * provider SDKs into the boot module graph and make the dynamic import in
 * `transport.ts` pointless.
 *
 * There is one capability field here and one effort ladder. Everything else the
 * two APIs can do is either shared by both — structured output, streamed text,
 * streamed reasoning summaries — or invisible to the six epistemic features,
 * and a capability nothing branches on is inventory rather than a capability.
 *
 * There ARE prices here now, and they arrived for one reason: a run can be given
 * a ceiling in money, and a ceiling has to be enforced against something. This
 * file used to say a dollar figure was unreconcilable and stop there, which was
 * the right call while nothing needed one — but "we cannot be exact" does not
 * mean "we cannot be bounded", and a bound that leans one way is worth more than
 * no number at all.
 *
 * So every rate below is a LIST price, read off the provider's own pricing page
 * on the date in `PRICE_READ_ON`, and `estimateUsd` is built so that it can meet
 * the bill but never sit below it:
 *
 *  - each kind of input a receipt names is charged at the rate that kind bills
 *    at — uncached at base, a cache write at its 1.25× or 2× premium, a cache
 *    read at the model's own read rate — and input a receipt does not name is
 *    charged at base, the dearest an input token bills at short of a write;
 *  - a page open is charged nothing where the provider publishes that it charges
 *    nothing, and every server-side request a receipt cannot name is charged at
 *    the search rate;
 *  - nothing here knows about batch discounts, negotiated rates or free credit,
 *    all of which only ever make the real charge smaller.
 *
 * Until D311 every input token was charged at base, which put the figure one
 * and a half to four times above the bill on purpose — a check that billed about
 * $0.40 read about $1. The person paying asked for the figure to be accurate
 * instead, and what survives is the direction: no term is ever priced below its
 * list rate, so on a complete receipt the figure meets the list charge and never
 * undercuts it, and wherever a receipt is incomplete it sits above.
 *
 * That one-sidedness is the whole design. It is also why no surface in this app may
 * call the output a cost, a bill or a total: it is an estimate that leans high,
 * every sentence printing it says so, and `PRICE_READ_ON` is printed with it so
 * a stale table is visible rather than merely wrong. A model with no `price` —
 * the custom endpoint, whose rates are between the user and whoever runs it —
 * disables the ceiling and says why, and never silently counts as free.
 *
 * Output ceilings are optional. `maxOutput` is clamped only when
 * present, and a placeholder `0` would make `Math.min(16000, 0)` request zero
 * output tokens on every pass of every model — surfacing as "No new distinct
 * moves found on that branch", an error that blames the reasoning for a
 * transport fact. Fill one in only from a provider's published limits, with the
 * date you read them.
 */

import type { ModelStamp, Spend } from './types'

/**
 * Which API a model is reached through. Four record types name one.
 *
 * `custom` is an endpoint the user supplies: their own proxy, a gateway, or a
 * model running on their own machine. It exists because the honest answer to "can
 * I use my Claude or ChatGPT subscription instead of an API key" is no — neither
 * provider offers a third-party sign-in that grants inference on a consumer
 * subscription, and no browser page can reach those web sessions across origins
 * anyway. What CAN be done is point this app at something the user controls, and
 * that is what this provider is.
 */
export type ProviderId = 'anthropic' | 'openai' | 'google' | 'custom' | 'typesafe'

/**
 * The effort ladder this app uses. Five levels, ascending.
 *
 * OpenAI also accepts `none` and `minimal`; both are deliberately absent. A
 * reasoning graph built on a model instructed not to reason is not this
 * product, and admitting the two literals is what would make that
 * configuration expressible.
 *
 * Both APIs spell these five identically (`output_config.effort` and
 * `reasoning.effort`), so there is nothing to translate. Anyone reaching for a
 * per-provider mapping table is solving a problem that does not exist.
 */
export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max'

export const EFFORT_LADDER: readonly Effort[] = ['low', 'medium', 'high', 'xhigh', 'max']

/** The ladder's levels as a control prints them. */
export const EFFORT_LABEL: Readonly<Record<Effort, string>> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
}

/** A stored string is a level only if the ladder says so. */
export const isEffort = (v: unknown): v is Effort =>
  typeof v === 'string' && (EFFORT_LADDER as readonly string[]).includes(v)

/**
 * The kinds of work this app asks a model to do, for anyone who wants to send
 * them to different models.
 *
 * Five and not nine: these are grouped by what a pass NEEDS, not by which
 * function calls it. `runVerify` and `runLinkScan` are one role because both
 * declare server-side search, and a user who points `check` at a model that can
 * search has fixed both at once — grouping them by their names instead would let
 * someone fix one and leave the other quietly broken. `runOp`, `runSeed` and
 * `realizeBridge` are one role for the mirror reason: they are the same pass with
 * different prompts, and splitting them would offer a choice with no consequence.
 *
 * Routing is entirely OPTIONAL and sparse. A role with nothing set runs on the
 * selected model, which is what every role does until someone changes one — so
 * this whole mechanism is invisible to anyone who does not go looking for it.
 */
export type Role = 'reason' | 'check' | 'panel' | 'scan' | 'chat'

/** Display order. The bulk of the spend first, because that is the one most
 *  worth moving and the reason anybody opens this. */
export const ROLES: readonly Role[] = ['reason', 'check', 'panel', 'scan', 'chat']

export const ROLE_LABEL: Record<Role, string> = {
  reason: 'Reasoning',
  check: 'Source checks',
  panel: 'Panels',
  scan: 'Finding connections',
  chat: 'Asking',
}

/** What actually runs under each role, named in the verbs the UI already uses so
 *  a reader can tell which button they are about to re-point. */
export const ROLE_NOTE: Record<Role, string> = {
  // Spelled the way the inspector spells them. "Counter" and "Assume" were
  // names for buttons that read Challenge and Invert — in the one sentence
  // promising to name the button you are about to re-point. Typed out rather
  // than built from `OP_LABEL`: this file imports nothing at runtime, and that
  // matters more than a list that cannot drift.
  reason:
    'Deepen, Challenge, Diverge, Test, Invert, Branch, Rewrite, and every move a run makes. Most of what you will spend.',
  check:
    'The source check, the link scan, the prior-art search and the field sweep — the passes that search the web.',
  panel:
    'All three seats of a panel. They always share one model: a disagreement has to be attributable to the lens, and three seats on three models could not show that.',
  scan: 'Find connections, on the Overview: the scan that looks for bridges between distant branches.',
  chat: 'Asking a question about a node or the graph. No search, so a conversation is the model’s own reading.',
}

/**
 * Whether a role declares server-side search, and therefore whether pointing it
 * at a `search: 'none'` model disables the feature rather than merely repricing
 * it.
 *
 * The one capability question routing has to ask. Everything else a model differs
 * on here is a matter of degree; this one turns a button off.
 */
export const ROLE_NEEDS_SEARCH: Record<Role, boolean> = {
  reason: false,
  check: true,
  panel: false,
  scan: false,
  chat: false,
}

/**
 * The kinds of work a person sets a thinking level for (D313): the five routes,
 * and the cross-examination, which has no route — it runs on another company's
 * model, which `resolveAuditor` chooses — but thinks, and bills, like any pass.
 *
 * The level is the PERSON'S. Until D313 the app decided it per pass, from a
 * table of named profiles and a temper that lifted every Reaching move a step.
 * Now each kind of work thinks at the level set for it under Settings → Routing,
 * every pass it covers alike, and nothing the app knows about a pass moves it:
 * not the temper, not the verb, not a packet's mix of verbs. The one thing that
 * still lowers a level is the model running it, because a model cannot think at
 * a level it does not accept: `resolveEffort` walks down to the model's top, in
 * the transport and nowhere else, and the row says so before the press.
 *
 * Keyed by route rather than by pass, because a route is what the person
 * already chooses a model for, and each route's note names the buttons it
 * covers — so the realised synthesis thinks with Reasoning and the link scan's
 * reasoning step with Source checks, as their models already follow those rows.
 */
export type EffortWork = Role | 'audit'

/** Display order: the routes as Routing lists them, then the cross-examination. */
export const EFFORT_WORK: readonly EffortWork[] = [...ROLES, 'audit']

export const EFFORT_WORK_LABEL: Readonly<Record<EffortWork, string>> = {
  ...ROLE_LABEL,
  audit: 'Cross-examinations',
}

/**
 * What each kind of work thinks at until somebody sets it: the level the app
 * gave that kind's passes before the choice was the person's, so a workspace
 * that never opens the rows runs as it did. Three passes the app had set apart
 * from their kind now think at their kind's level (D313): a Reaching move and a
 * realised synthesis, `xhigh` before and Reasoning's `high` now, and the link
 * scan's reasoning step, `xhigh` before and Source checks' `high` now.
 */
export const DEFAULT_EFFORT: Readonly<Record<EffortWork, Effort>> = {
  reason: 'high',
  check: 'high',
  panel: 'high',
  scan: 'xhigh',
  chat: 'low',
  audit: 'high',
}

/**
 * The levels a person has set. SPARSE, for `roleModels`' reason: an absent kind
 * thinks at its default, and the default is what the row shows as chosen, so a
 * level is written only when somebody picks one.
 */
export type EffortLevels = Partial<Record<EffortWork, Effort>>

/** The level one kind of work asks for: the one set for it, else its default. */
export const levelFor = (levels: EffortLevels | undefined, work: EffortWork): Effort =>
  levels?.[work] ?? DEFAULT_EFFORT[work]

/**
 * How deep a search comes back — the ONE capability the two APIs differ on that
 * this product cannot paper over.
 *
 * A named union rather than a boolean, because `results` is not a degraded
 * `results+text`: they are different strengths of evidence, and the naming is
 * part of the guarantee. There is no `'none'`: no model in this table lacks web
 * search, and a value nothing branches on would buy a three-state verify button
 * whose two extra branches can never render.
 */
export type SearchDepth =
  /**
   * No server-side search at all.
   *
   * Restored to this union for the custom endpoint, which is a URL the user typed:
   * this app cannot know whether whatever is behind it has a web-search tool, and
   * declaring one that is not there is a 400 on a pass the user paid for. So the
   * source check refuses itself on this depth and says why, which is the honest
   * degradation — the alternative is a check that returns nothing and reads as
   * "nothing found", the one confusion this whole feature exists to prevent.
   */
  | 'none'
  /** Results come back and pages can be opened; the page's own text never does. */
  | 'results'
  /** An opened page returns its text, so a quote can be checked against it. */
  | 'results+text'

/**
 * A provider: one key, one host, several models.
 *
 * Split from `ModelEntry` because the key is per provider — the app can hold
 * two at once — and because `host` is read by three surfaces that used to
 * hard-code `api.anthropic.com`: the key field's "where this key goes" note,
 * the network-error message about corporate networks, and the warning in
 * Sources.tsx about where the user's own passages travel to.
 */
export interface ProviderEntry {
  readonly id: ProviderId
  readonly name: string
  /** The key field's eyebrow. */
  readonly keyLabel: string
  /** The placeholder. Shape only — never an example that could be mistaken for a key. */
  readonly keyHint: string
  /**
   * Where a request goes, for the three surfaces that name it.
   *
   * Empty for the custom provider, whose host is whatever the user typed — every
   * surface reads `hostOfBase()` there instead, so the sentence about where a key
   * and a passage travel to names the real destination rather than a placeholder.
   */
  readonly host: string
  readonly consoleUrl: string
  /** True when this provider's endpoint is supplied by the user rather than known
   *  here. Two things branch on it: the setup sheet grows a URL field, and the
   *  adapter takes a `baseURL`. */
  readonly custom?: boolean
  /**
   * True for a house that answers typed questions and generates nothing.
   *
   * It has a key slot and a host like the others and NO `ModelEntry` rows: the
   * picker never offers it, `resolveModel` can never land on it, and no role can
   * be routed to it. What it can do is in `JUDGES` below, and the one place a
   * pass may ask it something is behind the same explicit press that pays for
   * the pass. See `docs/decisions.md` D106.
   */
  readonly judge?: boolean
}

/**
 * What a provider charges, at list, per model.
 *
 * Three base rates, and two that are optional ON PURPOSE. A cache read and a page
 * open each bill below the rate `estimateUsd` would otherwise charge them at —
 * base, and the search rate — so each is honoured only where it was read off the
 * provider's own page, and a row that omits one charges that kind at the dearer
 * rate. An absent field can make the figure high; it can never make it low. That
 * is also why there is no cache-WRITE rate here: both providers publish writes as
 * a multiple of base, so `writePremiumTokens` derives them, and a field beside it
 * would be a second copy that could drift below the first.
 *
 * Both providers happen to charge the same $10 per 1,000 searches. Kept per
 * model rather than hoisted to a constant, because that is a coincidence of two
 * price lists and not a fact about search.
 */
export interface ModelPrice {
  /** USD per million input tokens, at the base (uncached) rate. */
  readonly inputPerM: number
  /** USD per million output tokens. Reasoning tokens bill as output on both. */
  readonly outputPerM: number
  /** USD per thousand server-side search requests, billed apart from tokens. */
  readonly searchPerK: number
  /** USD per million input tokens served from the prompt cache. Absent: charged
   *  at `inputPerM`, which is never below what a read bills. */
  readonly cacheReadPerM?: number
  /** USD per thousand page opens, where the provider bills them apart from
   *  searches. Absent: charged at `searchPerK`, which is never below it. */
  readonly fetchPerK?: number
  /** A published input threshold changes token rates for the WHOLE request.
   *  Cache-write premiums use this input rate too; searches keep their own rate.
   *  A read uses this tier's own read rate, or this tier's input rate without one
   *  — never the short tier's, which is the lower of the two. */
  readonly longContext?: {
    readonly aboveInputTokens: number
    readonly inputPerM: number
    readonly outputPerM: number
    readonly cacheReadPerM?: number
  }
}

/**
 * The date every rate below was read off the provider's own pricing page.
 *
 * Printed beside every figure this file produces. A price table with no date
 * cannot be audited and cannot be known to be stale — and a silently stale table
 * is exactly how a ceiling in money stops meaning what the user set it to.
 *
 * It is the OLDEST reading in the table, not the newest, and that is the only
 * direction it may ever move by accident. Rows added or re-read later carry their
 * own read date in a comment, so this constant can understate how fresh a row is
 * and never overstate it. Printing the newest date instead would let one re-read
 * row vouch for every stale one beside it, which is the failure this date exists
 * to make visible. Raise it only as far as the oldest row it covers.
 *
 * On 29 September 2026 every model row was read again, both providers' pages the
 * same day (D311). The judge's rate was not — TypeSafe publishes none this app
 * could find — so the oldest reading in the table is now the judge's own, 20
 * September, and that is the date printed.
 */
export const PRICE_READ_ON = '20 September 2026'

/**
 * A model, as this app understands one.
 *
 * Streamed summarised reasoning and schema enforcement are ADMISSION CRITERIA
 * for this table rather than fields on it: the Thoughtstream and the
 * incremental unfurl are not features that can be off, so a model that cannot
 * do them is not a candidate. Stated here so nobody adds a `thinking: boolean`
 * that is `true` in every row.
 */
export interface ModelEntry {
  /** What the API answers to. Also what a `ModelStamp` records. */
  readonly id: string
  readonly provider: ProviderId
  readonly name: string
  /** One written line in the picker: what this model is FOR, in this tool.
   *  Never a paraphrase of a spec sheet, never a claim no code path keeps. */
  readonly note: string
  readonly search: SearchDepth
  /**
   * The levels this model accepts, ascending, non-empty, and CONSERVATIVE.
   *
   * A level listed here that the API rejects is a 400 on a pass the user
   * already committed to; a level omitted that would have worked merely runs
   * the pass a little cheaper. `resolveEffort` only ever clamps downward, so
   * the sole error this list can cause is the harmless one.
   *
   * INVARIANT: every row contains `'high'`, the level most kinds of work start
   * at (`DEFAULT_EFFORT`), so a workspace that has set nothing never has a level
   * lowered under it — its source checks and panel seats least of all.
   *
   * INVARIANT: every row is a contiguous prefix of `EFFORT_LADDER`, which is
   * what makes `effortNote`'s "goes up to x" exact.
   */
  readonly efforts: readonly Effort[]
  /** Output ceiling, when it has been read off published limits. Optional, and
   *  that is load-bearing — see the note at the top of this file. */
  readonly maxOutput?: number
  /**
   * List prices, or absent when this app cannot know them.
   *
   * Optional and NOT defaulted. A zero-filled row would make an unpriced model
   * read as a free one, and a spending ceiling that silently never fires is
   * worse than no ceiling — the user set a number and would watch it be
   * ignored. Every reader of this field handles the absence out loud.
   */
  readonly price?: ModelPrice
  /**
   * Rows here that answer what this row's safety filter declines, by the
   * category the provider names the refusal with (D312).
   *
   * Only where it is KNOWN, from the provider's own account of which filters a
   * model carries or from a pass this app watched each model take, and each
   * entry says which beside it. A row absent from a category's list is not a
   * claim that it would decline too; a category absent from the map is a claim
   * about nothing. The message a declined pass prints reads this and nothing else
   * (`declinedMessage`), so it can name a model to try without guessing — and the
   * person still chooses whether to route anything there. Nothing in this app
   * sends a declined request to another model on its own (invariant 3).
   */
  readonly answeredElsewhere?: Readonly<Partial<Record<string, readonly string[]>>>
}

/**
 * The registry rows that answer what `modelId`'s safety filter declined under
 * `category`, in the order the row lists them — or none where nothing is known.
 * A listed id this build no longer carries is dropped rather than printed.
 */
export function answeredElsewhere(modelId: string, category: string | undefined): ModelEntry[] {
  if (!category) return []
  const ids = findModel(modelId)?.answeredElsewhere?.[category] ?? []
  return ids
    .map((id) => findModel(id))
    .filter((m): m is ModelEntry => m !== undefined && m.id !== modelId)
}

/**
 * A chosen model plus the key that can run it.
 *
 * The single argument the transport takes, and the single thing an op stamps
 * onto what it produces. It replaces the old bare `apiKey`: with two providers
 * a key on its own does not say which API to send it to, and a key sent to the
 * wrong host has already leaked.
 */
export interface ResolvedModel {
  readonly entry: ModelEntry
  /** Empty is legal, and only for a custom endpoint: a model on this machine has
   *  no credential to send. Every other provider resolves to null rather than to
   *  an empty key. */
  readonly apiKey: string
  /** Set only for a custom endpoint. Its presence is what makes the adapter send
   *  the request somewhere other than the provider's own host. */
  readonly baseURL?: string
  /**
   * Which OpenAI-shaped API a custom endpoint speaks. Absent means Responses,
   * which is what every custom endpoint was until this field existed; `chat`
   * is Chat Completions, which is what Ollama and LM Studio serve. Set only for
   * a custom endpoint — each hosted provider has one API and the adapter never
   * asks.
   */
  readonly dialect?: 'responses' | 'chat'
}

export const PROVIDERS: Record<ProviderId, ProviderEntry> = {
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic',
    keyLabel: 'Anthropic API key',
    keyHint: 'sk-ant-…',
    host: 'api.anthropic.com',
    consoleUrl: 'https://console.anthropic.com/settings/keys',
  },
  openai: {
    id: 'openai',
    name: 'OpenAI',
    keyLabel: 'OpenAI API key',
    keyHint: 'sk-…',
    host: 'api.openai.com',
    consoleUrl: 'https://platform.openai.com/api-keys',
  },
  google: {
    id: 'google',
    name: 'Google',
    keyLabel: 'Gemini API key',
    keyHint: 'AIza…',
    host: 'generativelanguage.googleapis.com',
    consoleUrl: 'https://aistudio.google.com/apikey',
  },
  custom: {
    id: 'custom',
    name: 'Your own endpoint',
    keyLabel: 'Key, if yours needs one',
    keyHint: 'leave empty for a local model',
    host: '',
    consoleUrl: '',
    custom: true,
  },
  typesafe: {
    id: 'typesafe',
    name: 'TypeSafe',
    keyLabel: 'TypeSafe API key',
    // Shape only. TypeSafe publishes no prefix this app has verified, so the hint
    // says what the field is for rather than pretending to know what a key looks
    // like — `keyShapeWarning` has nothing to warn about here for the same reason.
    keyHint: 'the key from your TypeSafe account',
    host: 'api.typesafe.ai',
    consoleUrl: 'https://typesafe.ai',
    judge: true,
  },
}

/**
 * The id of the custom row, and the `model` string sent when nothing else is set.
 *
 * The endpoint's real address and its real model name are the user's own strings
 * and live in the store beside the keys, because neither can be validated against
 * anything in here. This row is the shape; those two fill it in.
 */
export const CUSTOM_MODEL_ID = 'custom-endpoint'

/**
 * The table. Grouped by provider, because the picker reads it in this order and
 * a chooser thinks in providers before models.
 */
export const MODELS: readonly ModelEntry[] = [
  {
    // First because it is the default (D109): the pre-selected radio and the
    // "Recommended" mark sit on the row a chooser reads first.
    id: 'claude-opus-5-5',
    provider: 'anthropic',
    name: 'Claude Opus 5.5',
    // The first run prints this as the default's one caveat, so it is written for
    // the person choosing: what happens and what to do, in plain words. "Its safety
    // classifiers decline more readily — biology included" alarmed both biology
    // users in a usability study and told neither what to do. The fact is D109's:
    // the classifiers add biology to what the previous default already declined.
    // That it also thinks longer per turn is in D109 and docs/guide.md.
    note: 'Searches as deeply as Opus 5 at a fifth less per token. It turns down some biology questions Opus 5 would answer; if one of yours is declined, try Opus 5.',
    // What its safety filter declines that other rows here answer (D312). Opus 5
    // is on the provider's own account: Opus 5.5's classifiers add biology to the
    // set Opus 5 carries (D109), which is also what the note above tells a
    // chooser. Sonnet 5 is on a measurement: on 30 September 2026 Opus 5.5
    // declined the token-efficiency eval's `enzyme-expand` fixture — an
    // enzyme-kinetics question — at medium and again at high, and Sonnet 5
    // answered the same request at high.
    answeredElsewhere: { bio: ['claude-opus-5', 'claude-sonnet-5'] },
    // Read 23 September 2026, the day after release. Both tools of the 2026-02-09
    // pair filter from inside code execution, and the search tool's page names
    // what fails: a model without programmatic tool calling has to be sent
    // `allowed_callers: ['direct']`, or the request is a 400. Programmatic tool
    // calling lists `claude-opus-5-5`, and the search page's own
    // dynamic-filtering example calls it by name.
    //
    // One page disagrees by omission, and it is recorded rather than smoothed
    // over: the fetch tool's list of dynamic-filtering models names neither this
    // model nor Opus 5. Opus 5, this app's default until D109, declares
    // `web_fetch_20260209` on every source check it runs, so that list is short
    // rather than exclusive. If a check here ever fails on the fetch tool, this
    // field was wrong, and 'none' is the fix, as on Fable 5.
    search: 'results+text',
    // platform.claude.com/docs/en/build-with-claude/effort, read 23 September
    // 2026: "Claude Opus 5.5 supports all five effort levels", and it is named in
    // both the `xhigh` and the `max` rows. Its API default is `medium` where Opus
    // 5's is `high`, which changes nothing here: both adapters send an effort on
    // every call.
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    // platform.claude.com/docs/en/models/opus-5-5/overview, read 23 September
    // 2026: the synchronous Messages API limit.
    maxOutput: 128000,
    // platform.claude.com/docs/en/about-claude/pricing, read 23 September 2026 and
    // again on 29 September. No long-context rate: the whole 1M window bills at
    // these. Cache writes are $5 and $8, the 1.25× and 2× `estimateUsd` already
    // adds. A cache READ is $0.20 — a twentieth of base, where most rows are a
    // tenth — and web fetch "has no additional charges beyond standard token
    // costs", so a page open is charged nothing (D311).
    price: { inputPerM: 4, outputPerM: 20, searchPerK: 10, cacheReadPerM: 0.2, fetchPerK: 0 },
  },
  {
    id: 'claude-opus-5',
    provider: 'anthropic',
    name: 'Claude Opus 5',
    note: "The previous default. This app's prompts and default settings were tuned on it.",
    search: 'results+text',
    efforts: ['low', 'medium', 'high', 'xhigh'],
    // platform.claude.com/docs/en/about-claude/pricing, read 29 September 2026: a
    // cache read at $0.50, a tenth of base. Web fetch is free on this provider,
    // and a receipt's breakdown names each open apart from each search, so an
    // open is charged nothing; a receipt with no breakdown still charges every
    // server request as a search (D311).
    price: { inputPerM: 5, outputPerM: 25, searchPerK: 10, cacheReadPerM: 0.5, fetchPerK: 0 },
  },
  {
    id: 'claude-fable-5',
    provider: 'anthropic',
    name: 'Claude Fable 5',
    note: 'For a claim whose mechanism is unusually hard, at higher list rates than Opus. It turns down more questions than the others, and a single pass can take minutes.',
    // NOT a claim that this model cannot search. A refusal to guess, which is the
    // same rule the custom endpoint follows for the same reason.
    //
    // Web FETCH is confirmed: the web fetch tool's page names Claude Fable 5 in
    // its dynamic-filtering support list (read 30 July 2026). Web SEARCH is not.
    // Fable 5's own launch page enumerates what it supports at launch — effort,
    // task budgets, the memory tool, code execution, programmatic tool calling,
    // context editing, compaction, vision — and web search is absent from that
    // list, while the search tool's page offers only a generic "Claude 4.6 and
    // later models" and the tool reference points back at the per-tool pages.
    // An enumeration that omits it and a generality that would include it are
    // not the same evidence, and the weaker one is not worth a paid 400.
    //
    // `npm run probe:fable` settles it in one request against your own key. If it
    // passes, this becomes 'results+text' and nothing else in this row changes.
    search: 'none',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    // platform.claude.com/docs/en/about-claude/models/introducing-claude-fable-5,
    // read 30 July 2026: "$10 USD per million input tokens and $50 USD per million
    // output tokens". `searchPerK` is carried even though `search` is 'none' — it
    // is unreachable while no pass here can search, and it is the rate that would
    // apply the instant the probe flips that field. A row whose price only becomes
    // correct once someone remembers to edit a second field is how a ceiling stops
    // holding without anyone noticing. Re-read on 29 September 2026 off the
    // pricing page, with its cache read at $1, a tenth of base (D311).
    price: { inputPerM: 10, outputPerM: 50, searchPerK: 10, cacheReadPerM: 1, fetchPerK: 0 },
  },
  {
    id: 'claude-sonnet-5',
    provider: 'anthropic',
    name: 'Claude Sonnet 5',
    note: 'Searches as deeply as Opus 5.5 at half its price. The one to leave running on a long unattended session.',
    // Confirmed rather than inferred: the web fetch tool's page names Claude
    // Sonnet 5 in its dynamic-filtering support list (read 30 July 2026), and on
    // this provider an opened page comes back as a document block carrying its
    // own text.
    search: 'results+text',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    // platform.claude.com/docs/en/about-claude/pricing, read 23 September 2026:
    // "The $2/$10 per million input/output token pricing for Claude Sonnet 5,
    // announced at launch as introductory pricing through August 31, 2026, is now
    // the standard price. The previously scheduled increase to $3/$15 … will not
    // occur." Still the LIST rate, as D49 requires. This row charged $3/$15 until
    // then because that was going to be the list rate, and it never became one
    // (D111). Re-read on 29 September 2026: a cache read at $0.20, a tenth of base,
    // and web fetch free (D311).
    price: { inputPerM: 2, outputPerM: 10, searchPerK: 10, cacheReadPerM: 0.2, fetchPerK: 0 },
  },
  {
    id: 'gpt-6-astra',
    provider: 'openai',
    name: 'GPT-6 Astra',
    note: 'For difficult research questions and extended reasoning. Higher list rates than any Sol.',
    search: 'results',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    // https://developers.openai.com/api/docs/models/gpt-6-astra and
    // https://developers.openai.com/api/docs/pricing, read 6 September 2026.
    // Responses supports streaming, structured output and web search. The same
    // adapter still receives no opened-page text. Above 272K input tokens the
    // higher input, cache-write and output rates apply to the whole request.
    //
    // Re-read 29 September 2026: unchanged, with cached input at $1 and $2 either
    // side of the threshold, a tenth of each input rate (D311). No `fetchPerK`: the
    // page bills web search at $10 per thousand CALLS and does not say whether a
    // page open is one, so an open is charged as a search.
    maxOutput: 128000,
    price: {
      inputPerM: 10,
      outputPerM: 50,
      searchPerK: 10,
      cacheReadPerM: 1,
      longContext: { aboveInputTokens: 272000, inputPerM: 20, outputPerM: 75, cacheReadPerM: 2 },
    },
  },
  {
    id: 'gpt-6.1-sol',
    provider: 'openai',
    name: 'GPT-6.1 Sol',
    note: 'The newest Sol, at a fifth of Astra’s list rates, and two-fifths of GPT-5.6 Sol’s input rate and a third of its output rate.',
    // developers.openai.com/api/docs/models/gpt-6.1-sol, read 11 October 2026:
    // Responses with streaming, structured output and the `web_search` tool, and
    // "`reasoning.effort` supports `low`, `medium` (default), `high`, `xhigh`, and
    // `max`". Tool calling is Responses-only — Chat Completions serves this model
    // without tools — which changes nothing here, since the hosted adapter speaks
    // Responses alone. Like every OpenAI row, the adapter receives no opened-page
    // text, so 'results'.
    search: 'results',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    maxOutput: 128000,
    // developers.openai.com/api/docs/pricing, read 11 October 2026: $2/$10 and
    // cached input at $0.10 up to 272K input tokens, $4/$15 and $0.20 above.
    // Cache writes are listed at $2.50 and $5, the 1.25× `estimateUsd` already
    // adds. Unlike GPT-5.6 Sol's, these rates carry no promotional note. No
    // `fetchPerK`, for the reason Astra's row gives.
    price: {
      inputPerM: 2,
      outputPerM: 10,
      searchPerK: 10,
      cacheReadPerM: 0.1,
      longContext: { aboveInputTokens: 272000, inputPerM: 4, outputPerM: 15, cacheReadPerM: 0.2 },
    },
  },
  {
    id: 'gpt-6-sol',
    provider: 'openai',
    name: 'GPT-6 Sol',
    note: 'GPT-6.1 Sol’s predecessor, at its input and output rates and twice its cached-input rate.',
    // developers.openai.com/api/docs/models/gpt-6-sol, read 11 October 2026:
    // Responses with streaming, structured output and the `web_search` tool, and
    // "`reasoning.effort` supports `none`, `low`, `medium` (default), `high`,
    // `xhigh`, and `max`" — `none` left out here as it is from every row (see
    // `Effort`). Chat Completions calls a function only at `none`, which nothing
    // here sends and the hosted adapter would never reach. The page points to
    // GPT-6.1 Sol as the newer model and carries no deprecation note.
    search: 'results',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    maxOutput: 128000,
    // developers.openai.com/api/docs/pricing, read 11 October 2026: $2/$10 and
    // cached input at $0.20 up to 272K input tokens, $4/$15 and $0.40 above, with
    // no promotional note. Cache writes are $2.50 and $5, the 1.25× `estimateUsd`
    // already adds. No `fetchPerK`, for the reason Astra's row gives.
    price: {
      inputPerM: 2,
      outputPerM: 10,
      searchPerK: 10,
      cacheReadPerM: 0.2,
      longContext: { aboveInputTokens: 272000, inputPerM: 4, outputPerM: 15, cacheReadPerM: 0.4 },
    },
  },
  {
    id: 'gpt-5.6-sol',
    provider: 'openai',
    name: 'GPT-5.6 Sol',
    note: 'An OpenAI option for complex reasoning at lower list rates than Astra.',
    search: 'results',
    efforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    // NOT the rate the pricing page lists today, deliberately (D377). On 11 October
    // 2026 developers.openai.com/api/docs/pricing lists $4/$20 and cached input at
    // $0.40, $8/$30 and $0.80 above 272K input tokens, under one footnote: "GPT-5.6
    // Sol’s promotional pricing is available at least through November 21, 2026."
    // OpenAI's own announcement on 21 August cut it "by over 20% for the next 3
    // months", from $5/$30 and $0.50 cached, and no page says what follows. D49
    // keeps a temporary promotion out of the ceiling, so this row charges the
    // standard rate the cut was taken from, until a page lists another. Above 272K
    // the model page's own rule applies, "2x input and 1.5x output for the full
    // request": $10/$45 and $1 cached. D311 moved this row to the promotional rate
    // on 29 September believing $5/$30 was gone from the list; it was only paused.
    price: {
      inputPerM: 5,
      outputPerM: 30,
      searchPerK: 10,
      cacheReadPerM: 0.5,
      longContext: { aboveInputTokens: 272000, inputPerM: 10, outputPerM: 45, cacheReadPerM: 1 },
    },
  },
  {
    id: 'gpt-5.6-terra',
    provider: 'openai',
    name: 'GPT-5.6 Terra',
    note: 'The balanced tier, at two-fifths of GPT-5.6 Sol’s list rates.',
    search: 'results',
    // CONSERVATIVE. Only `max` is documented as Sol-only, so `xhigh` is left off
    // until the API confirms it. An omitted level runs a pass one step cheaper;
    // an over-claimed one 400s a pass the user already committed to.
    efforts: ['low', 'medium', 'high'],
    // developers.openai.com/api/docs/pricing, read 29 September 2026: $2/$12 and
    // cached input at $0.20 up to 272K input tokens, $4/$18 and $0.40 above. It
    // charged $2.50/$15 until then (D311).
    price: {
      inputPerM: 2,
      outputPerM: 12,
      searchPerK: 10,
      cacheReadPerM: 0.2,
      longContext: { aboveInputTokens: 272000, inputPerM: 4, outputPerM: 18, cacheReadPerM: 0.4 },
    },
  },
  {
    id: 'gpt-5.6-luna',
    provider: 'openai',
    name: 'GPT-5.6 Luna',
    note: 'The least expensive OpenAI option here. Reason with it while you are still deciding what to ask.',
    search: 'results',
    efforts: ['low', 'medium', 'high'],
    // developers.openai.com/api/docs/pricing, read 29 September 2026: $0.20/$1.20
    // and cached input at $0.02 up to 272K input tokens, $0.40/$1.80 and $0.04
    // above. It charged $1/$6 until then, five times the rate now listed (D311).
    price: {
      inputPerM: 0.2,
      outputPerM: 1.2,
      searchPerK: 10,
      cacheReadPerM: 0.02,
      longContext: {
        aboveInputTokens: 272000,
        inputPerM: 0.4,
        outputPerM: 1.8,
        cacheReadPerM: 0.04,
      },
    },
  },
  {
    id: 'gemini-2.5-pro',
    provider: 'google',
    name: 'Gemini 2.5 Pro',
    note: 'A third house, for cross-examination: neither Anthropic nor OpenAI, so a claim can be examined by a provider that did not write it whichever of the other two did. Reasons and audits; does not search.',
    // NOT a claim that Gemini cannot search. A refusal to guess, with a reason
    // that is about THIS app rather than the model: grounding returns every
    // source as a redirect through vertexaisearch.cloud.google.com, so the URL
    // the citation gate would admit is never the URL the page lives at, and
    // `refutingDomains` would count one Google host however many domains the
    // sources were on. Wiring that in would clear invariant 2's bar with copies
    // of one host, which is the exact thing the bar was raised against. 'none'
    // until an adapter resolves the redirects before the gate sees them.
    search: 'none',
    // Sent as a thinking BUDGET rather than a level — see the three integers in
    // `engine/providers/google.ts`. `high` is here because every row must carry
    // it; nothing above it is offered because the ladder has no fourth budget.
    efforts: ['low', 'medium', 'high'],
    // No `price`, and the absence is deliberate for the reason the custom row
    // gives: the list page could not be read when this row was written, and a
    // figure from memory could lean low, which is the one direction invariant 5
    // forbids. The ceiling is unavailable on this row and says so; the audit it
    // exists for prints "no figure" rather than a guess. Add the rate when it
    // has been read off ai.google.dev/gemini-api/docs/pricing, with the date.
  },
  {
    id: 'gemini-2.5-flash',
    provider: 'google',
    name: 'Gemini 2.5 Flash',
    note: 'The cheaper Google tier. Enough for a cross-examination, which is what this house is here for.',
    search: 'none',
    efforts: ['low', 'medium', 'high'],
  },
  {
    id: CUSTOM_MODEL_ID,
    provider: 'custom',
    name: 'Your own endpoint',
    // Names the two APIs rather than the vendor, because the two are not the
    // same question and this row once got it wrong in the expensive direction:
    // every call went through `client.responses.create` while the note
    // recommended Ollama and LM Studio, which speak only Chat Completions. The
    // adapter now speaks both, and WHICH is the user's choice in the sheet —
    // carried on `ResolvedModel.dialect`, which the adapter branches on and
    // nothing else reads — so the note says which servers speak which rather
    // than guessing from an address.
    note: "Anything that speaks one of OpenAI's two APIs — the Responses API (/v1/responses) or Chat Completions (/v1/chat/completions), chosen in settings: a proxy you run, a gateway your work provides, or a model on this machine through Ollama or LM Studio, which speak Chat Completions.",
    // Not a guess about what the endpoint can do — a refusal to guess. See
    // `SearchDepth`'s `'none'`.
    search: 'none',
    // The effort field is sent as `reasoning.effort`, which a local server will
    // ignore and a proxy will pass on. `high` is here because every row must
    // contain it; the two above it are for a gateway that does honour it.
    efforts: ['low', 'medium', 'high'],
    // No `price`, and the absence is the honest answer rather than an omission.
    // What this endpoint charges is between the user and whoever runs it: a
    // gateway may mark up, a model on this machine costs electricity, and a proxy
    // in front of a provider may be either. Guessing zero would print "$0.00
    // estimated" over real money; guessing a provider's rate would print a
    // stranger's price list. So the spend ceiling is unavailable here and says so.
  },
]

/* ── The judge ────────────────────────────────────────────────────────────
   A second table, and deliberately not four more rows in the one above.

   A judge is a model that returns typed answers with probabilities and never a
   sentence: no streaming, no reasoning summary, no schema to enforce, no search.
   Every invariant `ModelEntry` carries — an effort ladder containing `high`, a
   search depth the check role branches on, a `maxOutput` to clamp — describes a
   capability a judge does not have, so a row here would be a row every reader of
   `MODELS` had to special-case. It has the two things the money path and the
   provenance stamp need, a price and an id, and nothing a pass could reason with.

   What a judge may be asked is bounded in `engine/judge.ts`, and what its answer
   may touch is bounded by D106: it orders, it annotates, and it is stamped. It
   never admits a citation, never earns or demotes a reading, never ends a run,
   and never picks a reasoning move. */

/** What a judge is called at its API, and what a judge stamp records. */
export interface JudgeEntry {
  readonly id: string
  readonly provider: ProviderId
  readonly name: string
  /** One written line: what this judge is FOR in this tool. */
  readonly note: string
  /**
   * List prices. REQUIRED, unlike `ModelEntry.price`: a judge is only ever asked
   * something inside a pass that is already under the ceiling, and a judge with
   * no rate would sink that ceiling on every check — so an unpriced judge is not
   * a row this table can hold.
   */
  readonly price: ModelPrice
}

/** A judge plus the key that can call it. The one argument `askJudge` takes. */
export interface ResolvedJudge {
  readonly entry: JudgeEntry
  readonly apiKey: string
  /**
   * Where the call goes. Absent, the adapter's own default — the API's host —
   * which is what the harness uses from Node. The page never resolves a judge
   * without one: see `JUDGE_ENDPOINT`.
   */
  readonly baseURL?: string
}

export const JUDGES: readonly JudgeEntry[] = [
  {
    id: 'jev-latest',
    provider: 'typesafe',
    name: 'Jev',
    note: 'Answers a yes/no, a choice or a score about text it is handed, with a probability. Used to order which claim a run checks first and to read each quoted source a second time; nothing it says lands as a node or a reading.',
    // TypeSafe's published rate as of 20 September 2026: $0.042 per million input
    // tokens, and output is not billed. An independent benchmark reconciled two
    // passes against the TypeSafe dashboard and found the charge matched input
    // alone, which is the reading this row rests on. `searchPerK` is zero because
    // a judge has no server-side tools — it cannot search, and a request sends no
    // tool at all — so there is nothing the search rate could ever apply to.
    // Re-read against docs.typesafe.ai before trusting a ceiling on it: the
    // vendor says the price is subsidised and expects it to move.
    price: { inputPerM: 0.042, outputPerM: 0, searchPerK: 0 },
  },
]

/** The judge a key resolves to. There is one, and `resolveJudge` names it. */
export const DEFAULT_JUDGE_ID = 'jev-latest'

/**
 * Where the PAGE may reach the judge, or null — and null is every default build.
 *
 * `npm run probe:typesafe` sent api.typesafe.ai the preflight a browser sends
 * and got a 400 carrying allow-methods and allow-headers and no allow-origin,
 * which is an origin allowlist this page is not on; TypeSafe's own guidance is
 * to keep the key server-side in a web app. This app has no server (D54), so on
 * a build that names nothing the page has no judge: `resolveJudge` is null
 * however many keys are stored, the settings panel offers no field for one, and
 * the judge's home is the evaluation harness, which calls the host from Node. A
 * build that sets `VITE_TYPESAFE_BASE_URL` — a proxy its owner runs, or the API
 * itself should TypeSafe allow the origin one day — turns the field on and sends
 * every call there. D107 has the options weighed.
 *
 * A pure function separated from the env read, on `selectableModels`'s pattern,
 * so both branches are testable without stubbing the build environment. A value
 * `baseUrlProblem` would refuse — plaintext to a host that is not loopback, or
 * not a URL at all — counts as unset rather than as an endpoint the key would be
 * sent to in the clear.
 */
export const judgeEndpoint = (raw: string | undefined): string | null => {
  const base = (raw ?? '').trim().replace(/\/+$/, '')
  return base && !baseUrlProblem(base) ? base : null
}

export const JUDGE_ENDPOINT: string | null = judgeEndpoint(
  import.meta.env.VITE_TYPESAFE_BASE_URL as string | undefined,
)

/**
 * The judge houses the settings panel offers a key field for: every provider
 * marked `judge` on a build that names an endpoint, none on one that does not.
 * A key field for a host the page cannot reach is a dead control — the same
 * reasoning that hides the custom row's card along with the custom row.
 */
export const judgeProviders = (endpoint: string | null): readonly ProviderId[] =>
  endpoint ? (Object.keys(PROVIDERS) as ProviderId[]).filter((p) => PROVIDERS[p].judge) : []

/**
 * The host a judge key is sent to, for the sentence under the field: the
 * build's endpoint where one is named, else the API's own. A proxy build must
 * not print `api.typesafe.ai` under a key that leaves for somewhere else.
 */
export const judgeHost = (endpoint: string | null): string => {
  if (!endpoint) return PROVIDERS.typesafe.host
  try {
    return new URL(endpoint).host
  } catch {
    return endpoint
  }
}

/**
 * The judge row for an id, by FAMILY rather than by exact id.
 *
 * `jev-latest` is an alias: the API answers with the dated version it resolved
 * to — `jev-1.13.0` at the time of writing — and the stamp names that version,
 * because the stamp names what answered (invariant 3). One price list covers the
 * whole family, so a ledger row stamped with a version this table has never seen
 * is still priced rather than sinking the ceiling. Anything outside the family
 * is null: an id the table cannot place is an id it cannot price.
 */
export const findJudge = (id: string): JudgeEntry | undefined =>
  JUDGES.find((j) => j.id === id) ?? JUDGES.find((j) => familyOf(j.id) === familyOf(id))

/** `jev-latest`, `jev-1.13.0` and `jev-preview` are one family, `jev`. */
const familyOf = (id: string): string => id.split('-')[0] ?? id

/**
 * The stamp for what actually answered, the judge's version of `stampFor`.
 *
 * `resolvedId` comes off the RESPONSE. The alias resolving to a dated version is
 * the expected case and keeps the family's name; an id outside the family names
 * itself, so a substitution grows a row naming the substitute rather than a
 * record quietly claiming the wrong author.
 */
export function judgeStampFor(e: JudgeEntry, resolvedId?: string): ModelStamp {
  const id = resolvedId || e.id
  const known = findJudge(id)
  return { provider: e.provider, id, name: known ? known.name : id }
}

/**
 * What a workspace that has not chosen a model runs on: its first pass, and every
 * pass after that until somebody picks a row.
 *
 * A workspace that picked one keeps it under `nv.model` whatever this says, so
 * moving this constant moves only the answer for a workspace that chose nothing —
 * the rule D80 set for the effort profile, applied to the model. D109 records the
 * move from Opus 5 to Opus 5.5, and what it owes.
 *
 * NOT what an old document is assumed to have run on. That is a claim about the
 * past, so `persist.ts`'s v2 migration names Opus 5 in a literal of its own and
 * never reads this: a default that moves forward must not rewrite who produced a
 * record already on disk.
 */
export const DEFAULT_MODEL_ID = 'claude-opus-5-5'

/**
 * The rows the picker OFFERS, which is not always every row the resolver KNOWS.
 *
 * The custom row is hidden on a build that sets `VITE_HIDE_CUSTOM_ENDPOINT`,
 * which is the deployed demo and nothing else. Two reasons, and neither is that
 * the row is bad: a strict `connect-src` cannot enumerate an address the user
 * types, so the one directive that stops an injected script posting a key to an
 * attacker's host cannot be written while that row exists; and an HTTPS page
 * reaching `http://localhost` is mixed content that Safari blocks outright, so
 * the local-model case the row mainly serves is already unreliable on a hosted
 * origin. Unset — every clone, every self-host — the row is present and the
 * product is whole, which is the boundary rule: the open-source project is the
 * complete thing, only the deploy is trimmed.
 *
 * `selectableModels` is a pure function, separated from the env read, so both
 * branches are testable without stubbing the build environment — and so the one
 * thing that must never drift is checked directly: hiding the row from the picker
 * must not remove it from `MODELS`, which `persist.ts` and `store.ts` resolve
 * stored ids against. A user who configured a custom endpoint before must not
 * have their saved session rejected on load.
 */
export const selectableModels = (hideCustom: boolean): readonly ModelEntry[] =>
  hideCustom ? MODELS.filter((m) => m.provider !== 'custom') : MODELS

const HIDE_CUSTOM = (import.meta.env.VITE_HIDE_CUSTOM_ENDPOINT as string | undefined) === 'true'

export const SELECTABLE_MODELS: readonly ModelEntry[] = selectableModels(HIDE_CUSTOM)

/**
 * The houses Settings → Providers shows a key card for, in the order it shows
 * them: every house the picker offers a model from, then the judge's on a build
 * that names its endpoint.
 *
 * One list for the panel that draws the cards and for `askForKey`, which singles
 * one out. A refusal that pointed the caret at a card the panel does not draw —
 * the judge's on the default build (D107), the endpoint's where its row is hidden
 * — would open the sheet on a sentence about a field that is not there.
 */
export const keyCardProviders = (
  endpoint: string | null,
  selectable: readonly ModelEntry[] = SELECTABLE_MODELS,
): readonly ProviderId[] => [
  ...(Object.keys(PROVIDERS) as ProviderId[]).filter((p) =>
    selectable.some((m) => m.provider === p),
  ),
  ...judgeProviders(endpoint),
]

export const findModel = (id: string): ModelEntry | undefined => MODELS.find((m) => m.id === id)

export const stampOf = (e: ModelEntry): ModelStamp => ({
  provider: e.provider,
  id: e.id,
  name: e.name,
})

/**
 * The stamp for what actually answered.
 *
 * `resolvedId` comes off the RESPONSE, not off the request. With no server-side
 * fallback declared anywhere the two always agree — and that is the point: if
 * they ever stop agreeing, the ledger grows a row naming the substitute rather
 * than a record quietly claiming the wrong author.
 */
export function stampFor(e: ModelEntry, resolvedId?: string): ModelStamp {
  if (!resolvedId || resolvedId === e.id) return stampOf(e)
  const known = findModel(resolvedId)
  return known ? stampOf(known) : { provider: e.provider, id: resolvedId, name: resolvedId }
}

/**
 * The effort a model will actually accept, at or below what was asked.
 *
 * Never rounds up. Walks down the ladder from `want` and takes the first level
 * this model lists; the floor is the model's own lowest, which by the table's
 * invariant is `low`. Called exactly once, in the dispatcher, so no caller and
 * no adapter can forget it.
 */
export function resolveEffort(e: ModelEntry, want: Effort): Effort {
  for (let i = EFFORT_LADDER.indexOf(want); i >= 0; i--) {
    const level = EFFORT_LADDER[i]!
    if (e.efforts.includes(level)) return level
  }
  return e.efforts[0] ?? 'high'
}

/* ── money ────────────────────────────────────────────────────────────────
   Four functions and one formatter. Every figure any surface in this app prints
   in dollars comes from here, so there is exactly one arithmetic to audit and
   exactly one set of assumptions to state. */

/** A ledger row, or anything shaped like one. Loose so a projection of a run that
 *  has not happened yet can be priced by the same function that prices one that
 *  did — two arithmetics for one question is how a pre-flight figure comes to
 *  disagree with the account afterwards. */
export interface Usage {
  inputTokens: number
  outputTokens: number
  searches: number
  /**
   * The parts of a `SpendDetail` this arithmetic reads, when the row carries one.
   *
   * Structurally typed rather than imported as `SpendDetail`, so a `Spend`
   * satisfies it, a projection that has no breakdown omits it, and this file goes
   * on importing nothing at runtime. The three writes are required and the rest
   * optional, because a projection names only the writes it declares — and a read
   * or a page open it does not name is priced as though it were not one, which is
   * the dearer way (D311).
   */
  detail?: {
    cacheWrite5m: number
    cacheWrite30m: number
    cacheWrite1h: number
    cacheRead?: number
    webFetches?: number
  }
  /** The largest single request in the row, where it is known — the size a
   *  long-context threshold is compared against. Absent, the row's own total
   *  is, which is right for a projection of one request and upward for a sum. */
  peakInputTokens?: number
}

/**
 * The premium a cache WRITE costs over the base rate it is already charged at.
 *
 * A five- or thirty-minute write bills at 1.25× base and a one-hour write at 2×,
 * so the extra over base is a quarter and a whole. `inputTokens` already carries
 * those tokens at base — each adapter reports an aggregate that includes them —
 * so what is missing is exactly this difference and not the whole charge.
 *
 * Zero for a row with no breakdown, which is every receipt written BEFORE the
 * meter: the figure such a row produces is unchanged. A PROJECTION carries one
 * on purpose — see `passCeiling` in `engine/run.ts`, where the reserve declares
 * its whole input allowance as a write because it cannot know how much of it
 * will be, and a bound that guesses low is not a bound.
 *
 * `inputUnclassified` is charged at base and not at a premium, and that is the
 * one place this term stops short. It is input whose category the adapter did
 * not report — on OpenAI it is a whole aggregate-only receipt, which carries no
 * write premium at all, so pricing it as a write would inflate every such row.
 *
 * That was a LIVE gap and not the hypothetical this comment first called it.
 * Anthropic's `cache_creation` split is optional beside the flat write total, and
 * the remainder used to land in `inputUnclassified` — a known write, charged at
 * base, on precisely the cold pass this premium exists for. The adapter files it
 * as a five-minute write now. What is left here is the genuine unknown: a write
 * category invented by a LATER build, which would land in unclassified and be
 * under-charged until somebody named it. Both adapters partition completely
 * today, and this is the sentence that has to be re-checked when one changes.
 */
const writePremiumTokens = (u: Usage): number =>
  ((u.detail?.cacheWrite5m ?? 0) + (u.detail?.cacheWrite30m ?? 0)) * 0.25 +
  (u.detail?.cacheWrite1h ?? 0) * 1.0

/**
 * Which short-lived cache a provider writes when this app names no longer TTL.
 *
 * A fact about billing, not about this app, and it is here because the term it
 * governs is here. A RECEIPT never needs it — an adapter reports the category
 * its own provider returned. A PROJECTION does: it has to name the whole input
 * allowance as the provider's possible cold write, because guessing a share
 * could make the reserve smaller than the pass it is meant to bound.
 *
 * Anthropic defaults an unnamed ephemeral breakpoint to five minutes. GPT-5.6
 * defaults `prompt_cache_options.ttl` to thirty minutes, and reports those writes
 * apart from uncached input. Both bill at 1.25×; keeping the classes distinct is
 * what stops a diagnostic receipt from calling thirty minutes five. `custom` is
 * null because a user-supplied endpoint has no published cache contract, and an
 * unpriced entry refuses a ceiling before this is read.
 */
export type CacheWriteClass = '5m' | '30m' | null

export const CACHE_WRITE_CLASS: Readonly<Record<ProviderId, CacheWriteClass>> = {
  anthropic: '5m',
  openai: '30m',
  // Gemini's implicit cache charges nothing to write — a hit is a discounted
  // read and a miss bills at base — so there is no premium for a projection to
  // name, and a null here says "no write class" rather than "unknown".
  google: null,
  custom: null,
  // A judge request is one small state and its questions, priced on input alone.
  // TypeSafe publishes no prompt cache, so there is no write class to name.
  typesafe: null,
}

/**
 * What a usage row would come to at list — meeting it, never undercutting it.
 *
 * Every input token starts at the base rate, and two terms move it from there,
 * each by exactly what a receipt NAMES and no further:
 *
 *  - a cache WRITE adds its premium (`writePremiumTokens`). A write bills at
 *    1.25× or 2× of base, so leaving it at base put the printed figure under the
 *    bill on a pass that writes without reading — a cold start, a changed prefix,
 *    an entry that expired before it was reused — which is the pass a ceiling most
 *    needs to see;
 *  - a cache READ gives back the difference between base and the row's own read
 *    rate, where the row publishes one. Until D311 reads stayed at base on
 *    purpose, and that was the headroom the old figure was built from: a check
 *    that re-reads a hundred thousand cached tokens read at about two and a half
 *    times what it billed. A row with no read rate still charges them at base.
 *
 * Server requests the same way: every one at the search rate, less what a named
 * page open saves where the row publishes a lower rate for one.
 *
 * Each give-back is bounded by what it comes out of — reads by the input they are
 * part of, opens by the requests they are part of — and a rate above the one it
 * replaces gives back nothing. So no named token or request is priced below its
 * list rate, and an unnamed one is priced at the dearest rate it could bill at
 * short of a write. That is the property the ceiling rests on.
 */
export const estimateUsd = (u: Usage, p: ModelPrice): number => {
  // A long-context rate is not a marginal surcharge on the excess tokens. It
  // reprices the entire request, including output, known cache writes and reads
  // — and it is a fact about ONE request, so a ledger row is tested by the
  // largest request in it where that is known, never by the sum of a session's
  // short ones (`Spend.peakInputTokens`, D311).
  const size = u.peakInputTokens ?? u.inputTokens
  const tokens = p.longContext && size > p.longContext.aboveInputTokens ? p.longContext : p
  const reads = Math.min(Math.max(0, u.detail?.cacheRead ?? 0), u.inputTokens)
  const readGiveBack = Math.max(0, tokens.inputPerM - (tokens.cacheReadPerM ?? tokens.inputPerM))
  const opens = Math.min(Math.max(0, u.detail?.webFetches ?? 0), u.searches)
  const openGiveBack = Math.max(0, p.searchPerK - (p.fetchPerK ?? p.searchPerK))
  return (
    (u.inputTokens * tokens.inputPerM) / 1e6 +
    (writePremiumTokens(u) * tokens.inputPerM) / 1e6 -
    (reads * readGiveBack) / 1e6 +
    (u.outputTokens * tokens.outputPerM) / 1e6 +
    (u.searches * p.searchPerK) / 1e3 -
    (opens * openGiveBack) / 1e3
  )
}

/** List prices for a stored model id, or null. Reads the registry rather than a
 *  stamp: a `ModelStamp` is a copy taken when a pass ran, and a model delisted
 *  since has no rate to charge — which the caller has to handle rather than
 *  round to zero. A judge's rows are priced by the same rule off `JUDGES`, so a
 *  ledger holding a judge row prices it rather than sinking the ceiling. */
export const priceOf = (id: string): ModelPrice | null =>
  findModel(id)?.price ?? findJudge(id)?.price ?? null

/**
 * The whole ledger as one estimate, plus what could not be priced.
 *
 * The second half of the return is the point. A ledger holding one row this file
 * has no rate for produces a number that is right about everything else and
 * silently short by that row — so the models it could not price come back beside
 * the figure, and every caller either names them or refuses. The ceiling in
 * `run.ts` refuses.
 */
export function estimateSpendUsd(rows: readonly Spend[]): {
  usd: number
  unpriced: ModelStamp[]
} {
  let usd = 0
  const unpriced: ModelStamp[] = []
  for (const r of rows) {
    const p = priceOf(r.model.id)
    if (!p) {
      unpriced.push(r.model)
      continue
    }
    usd += estimateUsd(r, p)
  }
  return { usd, unpriced }
}

/**
 * Dollars, for a reader. Always two places, always rounded UP to the cent.
 *
 * Up rather than to-nearest because every other figure in this file leans the same
 * way, and a printed $4.999 shortened to "$4.99" would be the one place the
 * arithmetic understated itself.
 *
 * Two places and not fewer, which took a correction: rounding to whole dollars
 * above ten looked like honest imprecision and was not, because this same function
 * prints the ceiling the USER typed. Someone who sets 12.50 and reads "$13" back
 * has been misquoted about their own limit, which is worse than any amount of false
 * precision — so cents everywhere, and no case that can reword a typed number.
 */
export const fmtUsd = (usd: number): string =>
  // The epsilon is not paranoia. $0.60 + $1.60 + $0.18 is 2.3800000000000003 in
  // binary floating point, and a bare `Math.ceil(x * 100)` turns an exact $2.38
  // into "$2.39" — a cent invented by the representation rather than by the
  // rounding rule, which makes the arithmetic look wrong at exactly the moment
  // someone checks it by hand. Small enough that a genuine fraction of a cent
  // still rounds up.
  `$${(Math.ceil(Math.max(0, usd) * 100 - 1e-9) / 100).toFixed(2)}`

/** The top of a model's ladder. */
const ceilingOf = (e: ModelEntry): Effort => e.efforts[e.efforts.length - 1] ?? 'high'

/**
 * What this model's search can and cannot do, for the picker.
 *
 * One sentence per `SearchDepth`, written once here and read everywhere, so the
 * picker, the source-check button and the check record cannot come to describe
 * the same capability in two ways.
 *
 * `'none'` splits in two, because it now arrives for two unrelated reasons and a
 * reader is owed the one that applies. A custom endpoint is unknowable — the app
 * cannot see what is behind an address the user typed. Fable 5 is merely
 * unconfirmed — the capability is a fact that exists, this app has not yet
 * established it, and that is a sentence about our evidence rather than about the
 * model. Telling a Fable 5 user their endpoint cannot be inspected would be
 * describing a situation they are not in.
 */
export function searchNote(e: ModelEntry): string {
  if (e.search === 'none') {
    // Plain words, because the first run prints these beside every row. WHY a
    // check refuses rather than guessing — declaring a search tool that is not
    // there fails the whole pass — is the header above and docs/guide.md.
    return e.provider === 'custom'
      ? 'No source checks on an endpoint you supplied: this app can’t tell whether it can search. Everything else works.'
      : 'Source checks are off on this model: this app has not confirmed it can search. Everything else works, and Settings → Routing can send checks to a model that does.'
  }
  return e.search === 'results+text'
    ? 'Searches the web, opens pages, and returns their text — so a quoted sentence can be checked against the page it came from.'
    : 'Searches the web and opens pages. Page text is not returned, so a quoted sentence cannot be checked against the page it came from.'
}

/**
 * What a source check on this model is offering to do, on the button itself.
 *
 * Here rather than in the component for the same reason `searchNote` is: one
 * capability, one sentence. Two inline variants in a JSX branch is how the button
 * comes to promise a quote check that the record then says never happened.
 */
export function verifyOfferNote(e: ModelEntry): string {
  if (e.search === 'none') {
    // Split for the reason given on `searchNote`: same field, two situations,
    // and the one a reader is in decides which sentence is true for them.
    return e.provider === 'custom'
      ? 'Not available on an endpoint you supplied: this app cannot know whether it has a web search, and a check with no search behind it would return nothing and read as “nothing found”. Switch to Opus 5, Sonnet 5 or a GPT‑5.6 tier for this one.'
      : 'Not available here yet: this app has not confirmed that this model can search, and a check with no search behind it would return nothing and read as “nothing found”. Switch to Opus 5 or Sonnet 5 for this one — both return the text of the pages they open.'
  }
  const base =
    'Searches, opens the strongest results, and reports what they say — and whether anyone has said it already.'
  return e.search === 'results+text'
    ? `${base} Several thousand tokens and a handful of searches.`
    : `${base} This model does not return the text of the pages it opens, so a quoted sentence cannot be checked against its page — and it decides for itself how many searches to run.`
}

/**
 * What sending this role to this model would cost you in capability, said at the
 * moment of choosing rather than at the moment of failing.
 *
 * Returns null when there is nothing to warn about, which is the usual case.
 * Deliberately NOT a block: routing `check` at a model that cannot search is a
 * legitimate thing to do on the way to routing it somewhere else, and a picker
 * that refuses the intermediate state is a picker that argues with you. The
 * feature turns itself off and says why, exactly as it already does when the
 * selected model cannot search.
 */
export function roleWarning(role: Role, e: ModelEntry): string | null {
  if (!ROLE_NEEDS_SEARCH[role] || e.search !== 'none') return null
  return e.provider === 'custom'
    ? `${e.name} has no search this app can declare, so the source check, the link scan, the prior-art search and the field sweep would all turn themselves off.`
    : `This app has not confirmed that ${e.name} can search, so the source check, the link scan, the prior-art search and the field sweep would all turn themselves off rather than risk a failed pass.`
}

/* ── Route presets ────────────────────────────────────────────────────────
   Workstream 5 of `docs/token-efficiency-implementation.md`, and the shape it
   insists on: "Pathways already supports explicit routing by role, so the first
   implementation is an evaluated preset rather than a hidden model ladder."

   A preset is DATA — five role/model pairs — and applying one writes the same
   five routes a person can set by hand, into the same draft the manual selects
   write into, committed by the same Save. There is no second path to routing and
   deliberately so: a preset that resolved a model of its own would be the hidden
   ladder the plan refuses, and invariant 4's refusal is written against
   `roleModels` rather than against however `roleModels` got its contents. */

export type RoutePresetId = 'efficient-quality'

export interface RoutePreset {
  id: RoutePresetId
  name: string
  /** Why somebody would pick it, in one line. Printed beside the control. */
  note: string
  /**
   * Where each role goes.
   *
   * Exhaustive over `Role`, so a sixth role stops the build until somebody
   * decides where this preset sends it — the same reason `ROLE_NEEDS_SEARCH` and
   * `RUN_VERB_MAX_YIELD` are exhaustive. A sparse preset would silently leave the
   * new role on the selection and read as a considered choice.
   *
   * Every value PINS, including the ones naming the model most people already
   * have selected. Pinning and clearing are different states — see
   * `setRoleModel` — and the difference is exactly what a preset is claiming:
   * "checks go to Opus 5.5" has to survive the user later selecting something
   * else, or the preset is a suggestion that quietly stops applying.
   */
  routes: Readonly<Record<Role, string>>
}

/**
 * The plan's candidate preset, and the reason it is not a default.
 *
 * Reasoning moves to Sonnet 5 — most of the spend, and the row the plan's
 * projections turn on — while the three passes whose failure modes are not
 * "slightly worse prose" stay on the default: a source check, a panel seat and
 * the closing scan. Asking follows reasoning, since a conversation about text
 * already on screen is the cheapest thing in the app and the least load-bearing.
 *
 * The plan named Opus 5 for those three, because Opus 5 was the default it was
 * written against. D110 moved them to Opus 5.5 with the default (D109): pinned to
 * Opus 5, a press called Efficient quality moved them off the default onto the
 * dearer row.
 *
 * OPT-IN, and it must stay opt-in: the plan says so in as many words — "The
 * preset must remain opt-in until the Sonnet reasoning arm passes" — and D44
 * recorded that no arm can reach a `pass` on the derive workload the harness
 * runs. So this ships as a control somebody presses, never as a default and never
 * as a fallback anything reaches for.
 *
 * The check role stays on a `results+text` row deliberately, and `roleWarning`
 * would say so if it did not: a preset that pointed the source check at a model
 * this app cannot confirm searches would turn the feature off to save money,
 * which is the one trade this product does not make.
 */
export const EFFICIENT_QUALITY: RoutePreset = {
  id: 'efficient-quality',
  name: 'Efficient quality',
  note: 'Reasoning and Asking on Sonnet 5; source checks, panels and the breakthrough scan stay on Opus 5.5.',
  routes: {
    reason: 'claude-sonnet-5',
    check: 'claude-opus-5-5',
    panel: 'claude-opus-5-5',
    scan: 'claude-opus-5-5',
    chat: 'claude-sonnet-5',
  },
}

export const ROUTE_PRESETS: readonly RoutePreset[] = [EFFICIENT_QUALITY]

/**
 * Whether a set of routes is exactly this preset.
 *
 * EXACT, over every role: a preset with one role moved is not that preset any
 * more, and a control that read it as applied would let somebody clear four
 * routes they never set while believing they were undoing one thing. The routes
 * are compared and not a stored flag, so the answer stays true after a manual
 * edit — there is nothing to keep in step, because there is nothing stored.
 */
export const presetApplied = (routes: Partial<Record<Role, string>>, p: RoutePreset): boolean =>
  ROLES.every((role) => routes[role] === p.routes[role])

/**
 * Named in the picker when a model's ladder stops below the top of the one a
 * person can choose from (D313): a kind of work set above it runs at its top.
 *
 * The clamp is silent in the transport by design; this is where it stops being
 * silent to the person choosing.
 */
export function effortCeilingNote(e: ModelEntry): string | null {
  const top = ceilingOf(e)
  if (top === EFFORT_LADDER[EFFORT_LADDER.length - 1]) return null
  return `Thinking tops out at ${EFFORT_LABEL[top]} on ${e.name}, so anything set higher runs at ${EFFORT_LABEL[top]} on it.`
}

/**
 * Printed under a kind of work's level, only when the model running it would
 * lower it.
 *
 * "goes up to x" is exact rather than approximate: every row's `efforts` is a
 * contiguous prefix of `EFFORT_LADDER`, so a clamp can only ever land on that
 * row's ceiling.
 */
export function effortNote(e: ModelEntry, want: Effort): string | null {
  const got = resolveEffort(e, want)
  if (got === want) return null
  return `${e.name} goes up to ${EFFORT_LABEL[got]}, so ${EFFORT_LABEL[want]} runs at ${EFFORT_LABEL[got]} on it.`
}

/**
 * The published key prefixes, in the order they must be asked.
 *
 * Order is the whole content. `'sk-ant-…'.startsWith('sk-')` is TRUE, so the
 * obvious per-provider prefix test accepts exactly the paste that matters — an
 * Anthropic secret bound for `api.openai.com` — and the key would leave the
 * page before anything rejected it. So the Anthropic prefix is asked first, and
 * OpenAI's bare `sk-` last. One table for the key field's guess and the
 * composer's (`keyHouseGuess`, `looksLikeProviderKey`), so the two cannot
 * disagree about whose key a string is.
 */
type KeyHouse = 'anthropic' | 'openai' | 'google'
const KEY_PREFIXES: readonly (readonly [string, KeyHouse])[] = [
  ['sk-ant-', 'anthropic'],
  ['AIza', 'google'],
  ['sk-', 'openai'],
]

/**
 * Whose key this looks like, by its prefix alone — or null for a string with no
 * prefix this app knows.
 *
 * A GUESS, and used as one: it names the house in the key field's warning and
 * offers that house's models (D335), and never decides where a key is sent or
 * whether it is kept. The provider a key is written under is still the card's
 * prop and nothing else (`ProviderKeyCard`).
 */
export function keyHouseGuess(key: string): KeyHouse | null {
  const k = key.trim()
  for (const [prefix, house] of KEY_PREFIXES) if (k.startsWith(prefix)) return house
  return null
}

/**
 * The whole shape of each house's key, past its prefix. Strict where the guess
 * above is loose: this is asked of free text, where "the sk- prefix" is prose and
 * must never read as a secret. Lengths are floors from the published formats.
 */
const KEY_BODIES: Record<KeyHouse, RegExp> = {
  anthropic: /^sk-ant-[A-Za-z0-9_-]{20,}$/,
  openai: /^sk-(?:(?:proj|svcacct)-[A-Za-z0-9_-]{20,}|[A-Za-z0-9]{32,})$/,
  google: /^AIza[0-9A-Za-z_-]{35}$/,
}

/**
 * Whether a piece of free text holds what looks like a provider's API key, and
 * whose — for the composer, where a key pasted into the wrong box would be sent
 * to a model, saved in the exploration and carried in a share link (D335).
 *
 * Known prefixes only, on whitespace-delimited tokens, with quotes and trailing
 * punctuation around a token ignored. Ordinary prose never trips it, because no
 * word of prose is `sk-ant-` followed by twenty key characters. The prefix is
 * read through `keyHouseGuess` and only that house's full shape is then asked:
 * an `sk-ant-` token too short to be a key is nobody's, never OpenAI's.
 */
export function looksLikeProviderKey(text: string): ProviderId | null {
  for (const raw of text.split(/\s+/)) {
    const token = raw.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9_-]+$/g, '')
    const house = keyHouseGuess(token)
    if (house && KEY_BODIES[house].test(token)) return house
  }
  return null
}

/**
 * The model a move to another house lands on: the recommended one when it is that
 * house's, otherwise the picker's first row for it. Null for a house the picker
 * does not offer (the judge; the endpoint row on a build that hides it), so a
 * switch is never offered to a house nothing could be selected in.
 */
export function houseModelId(p: ProviderId): string | null {
  if (findModel(DEFAULT_MODEL_ID)?.provider === p) return DEFAULT_MODEL_ID
  return SELECTABLE_MODELS.find((m) => m.provider === p)?.id ?? null
}

/** "an OpenAI API key", "a Gemini API key" — the key field's own label, articled. */
const aKeyOf = (p: ProviderId): string => {
  const label = PROVIDERS[p].keyLabel
  return `${/^[AEIOU]/.test(label) ? 'an' : 'a'} ${label}`
}

/**
 * A warning about a pasted key, and never a block.
 *
 * Another house's key is named as that house's (`keyHouseGuess`), in one sentence
 * for every field. The Anthropic field used to be the asymmetric one: an `sk-proj-`
 * or `AIza` key there read only "save it anyway if you are sure", which never said
 * whose key it was and invited the save — on the card the splash opens on, where a
 * newcomer pastes the key they already have (D335). A key with no prefix this app
 * knows keeps that sentence; nothing better is known about it.
 *
 * Nothing here refuses a save. A validator that locks a paying user out of
 * their own key is worse than a rejected request whose error they can read.
 */
export function keyShapeWarning(p: ProviderId, key: string): string | null {
  const k = key.trim()
  if (!k) return null
  // Nothing is known about what a custom endpoint wants, so there is no shape to
  // warn about. Warning anyway would be inventing a rule. The same for the judge:
  // TypeSafe's key format is not something this app has verified, and a warning
  // written from a guess would send people back to their dashboard over a key
  // that works.
  if (p === 'custom' || p === 'typesafe') return null
  const house = keyHouseGuess(k)
  if (house === p) return null
  if (house) {
    return `That looks like ${aKeyOf(house)}. This field sends to ${PROVIDERS[p].host}, which will reject it — and the key would have left this page to get there.`
  }
  const prefix = KEY_PREFIXES.find(([, h]) => h === p)![0]
  return `${PROVIDERS[p].keyLabel}s begin with ${prefix}. This one does not — save it anyway if you are sure.`
}

/**
 * Why the cross-examination row is not offered, where it is not: the composer's
 * tune panel and Settings → Defaults say it in these words, so the two surfaces
 * that set the same count cannot drift on why it is missing (D335).
 */
export const CROSS_EXAM_NEEDS_SECOND_KEY =
  'A cross-examination needs a key for a second provider, so a model from another company can attack what this one wrote. One from the same company would be the panel again.'

/** Where to get a key, as a host a reader recognises. */
export function consoleHost(p: ProviderEntry): string {
  try {
    return new URL(p.consoleUrl).host
  } catch {
    return p.consoleUrl
  }
}

/**
 * The host of a base URL, for the sentences that name where something travels to.
 *
 * Falls back to the raw string rather than to a placeholder: a user who typed
 * something unparseable must see what they typed in the sentence about where their
 * prompt is going, not a reassuring hostname the app invented.
 */
export function hostOfBase(base: string): string {
  try {
    return new URL(base).host
  } catch {
    return base || 'the endpoint you set'
  }
}

/**
 * Whether a base URL is safe enough to send a prompt and possibly a key to.
 *
 * `https` anywhere, or `http` only on a loopback host — a local model server is
 * the one legitimate plaintext case, and allowing plaintext to any host would let
 * a mistyped address carry the user's prompt and key across the network in the
 * clear. Returns the reason rather than a boolean, because the field prints it.
 */
export function baseUrlProblem(base: string): string | null {
  const raw = base.trim()
  if (!raw) return 'Needs an address — the app has nothing to call without one.'
  let u: URL
  try {
    u = new URL(raw)
  } catch {
    return 'That is not a URL the browser can parse. It needs a scheme, like https://…'
  }
  // Loopback as share.ts counts it: `*.localhost` resolves to this machine, and so
  // does all of 127.0.0.0/8. The 127 test is on a dotted quad, which the URL parser
  // has already normalised, so a host NAMED `127.example.com` is not loopback.
  const h = u.hostname
  const local =
    h === 'localhost' ||
    h.endsWith('.localhost') ||
    /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h) ||
    h === '[::1]'
  if (u.protocol === 'http:' && !local) {
    return 'Only https, except on localhost. Over plain http your prompt — and the key, if you set one — would cross the network readable by anything in between.'
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:')
    return 'Needs to be an http or https address.'
  return null
}
